<template>
  <div class="job-toggler">
    <el-switch v-model="internalJobsEnabled" />
    <div :class="{'disabled' : !internalJobsEnabled}">
      <p class="small">
        {{TextAssistant.itemPluralName(entityType)}} will be assigned to the appropriate job whenever the status changes.
      </p>
      <el-tooltip v-if="hasStatusJobMappings" popper-class="status-job-map" placement="top">
        <template #content>
          <div v-for="(mapping, index) in statusJobMappings" :key="index" class="mapping"> 
            <status-chicklet :status="getStatusByID(mapping.parentID)" />
            <i class="ddi ddi-arrow-right" />
            <chicklet is-uppercase size="tiny">{{ getJobByID(mapping.childID).name }}</chicklet>
          </div>
        </template>
        <i class="ddi ddi-info" />
      </el-tooltip>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IIDTuple,
  IItemForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'

/* API */

export default defineComponent({
  name: 'JobToggler',

  props: {
    jobsEnabled: {
      type: Boolean,
      required: true,
    },
    showWarningWhenDisabling: {
      type: Boolean,
      default: false,
    },
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    statusJobMappings: {
      type: Array as () => IIDTuple[],
      default: null,
    },
    statuses: {
      type: Array as () => IStatusForSelection[],
      default: null,
    },
    jobs: {
      type: Array as () => IItemForSelection[],
      default: null,
    },
  },

  emits: ['update:jobsEnabled'],

  data() {
    return {
      TextAssistant,
      showDropdown: false,
    }
  },

  computed: {
    internalJobsEnabled: {
      get(): boolean {
        return this.jobsEnabled
      },
      set(value: boolean) {
        if (this.showWarningWhenDisabling && !value) {
          this.$confirm(
            `By turning off jobs, you are choosing to manually assign ${TextAssistant.itemPluralName(
              this.entityType,
            ).toLowerCase()}. Jobs will be removed from ${TextAssistant.itemPluralName(
              this.entityType,
            ).toLowerCase()} and custom filters. There is no undo. Still continue?`,
            {
              dangerouslyUseHTMLString: true,
              center: true,
              type: 'warning',
              title: 'Turn off jobs',
            },
          ).then(() => {
            this.$emit('update:jobsEnabled', value)
          })
        } else {
          this.$emit('update:jobsEnabled', value)
        }
      },
    },
    hasStatusJobMappings(): boolean {
      return (
        this.statusJobMappings !== null && this.statusJobMappings.length > 0
      )
    },
  },

  methods: {
    getStatusByID(id: number) {
      return this.statuses.find(s => s.id === id)
    },
    getJobByID(id: number) {
      return this.jobs.find(j => j.id === id)
    },
  },
})
</script>
<style lang="scss">
.job-toggler {
  display: flex;
  align-items: center;
  border: $border--dotted-light;
  border-radius: 50px;
  background-color: var(--color-background-primary);
  padding: 10px;
  margin-bottom: 20px;
  @include flex-row-horizontal-spacing(8px);

  > div {
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(8px);

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }

    .ddi-info {
      cursor: pointer;
      font-size: 28px;
      color: var(--color-text-third);
    }
  }
}
</style>
